import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import { View } from "react-native";
import VStack from "../../../ui_atoms/VStack";
import { useForm, Controller } from "react-hook-form";
import HStack from "../../../ui_atoms/HStack";
import Gap from "../../../ui_atoms/Gap";
import {
    calculatePayrollBreakdownMaxWorkDays,
    FormData,
    PayrollBreakdownEditorProps,
} from "./PayrollBreakdownEditor";
import DropdownField from "../../DropdownField";
import { BodyMediumText, BodySmallText } from "../../FormattedText";
import PayrollBreakdownCommon from "./PayrollBreakdownCommon";
import PayrollBreakdownTotalInfo from "./PayrollBreakdownTotalInfo";
import PayrollBreakdownMonthlyInfo from "./PayrollBreakdownMonthlyInfo";
import { useTheme } from "../../../hooks/use-theme";
import {
    GET_MISSION_OVERTIME_STATUS,
    GET_MISSION_VACATION_ACCUMULATION_MINUTES_FOR_PAYROLL_BREAKDOWN,
} from "../../../graphql/timesheet";
import { useQuery } from "@apollo/client/react";
import { SHORT_DATE, useDateFormatter } from "../../../hooks/date";
import FullWidthLine from "../../FullWidthLine";
import { useDeepCompareEffect } from "../../../hooks/use-deep-compare-effect";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import QuantityField from "../../QuantityField";
import { buildPayrollBreakdownFormFromModel } from "./utils";
import MarkdownView from "../../MarkdownView";
import { useOpenMarkdownLink } from "../../../hooks/use-open-markdown-link";
import PayrollBreakdownExtras, { PayrollBreakdownExtrasProps } from "./PayrollBreakdownExtras";
import { toFixedIfNecessary } from "../../../utils";

const PayrollBreakdownEditorMonthly = ({
    payrollBreakdown,
    vacationStatus,
    onValidData,
    onChange,
    needWorkDays,
    isFromTimesheet,
    timeSheetsNeedCompletion,
    mission,
    isEmptyPeriod,
    setCustomSummaryRows,
}: PayrollBreakdownEditorProps) => {
    const i18n = useI18n();
    const { colors, fonts } = useTheme();
    const formatDate = useDateFormatter();
    const hoursPerDay = useMemo(() => {
        return parseFloat(vacationStatus?.contractHoursPerWeekFullTime || "0") / 5;
    }, [vacationStatus]);

    const [vacationInputType, setVacationInputType] = useState<"days" | "hours" | "minutes">(() =>
        vacationStatus.contractVacationMinutes < 2500 ? "hours" : "days",
    );

    const [usedVacationInputValue, setUsedVacationInputValue] = useState<number | undefined>(() => {
        if (payrollBreakdown.usedVacationMinutes == null) return 0;
        const hpd = parseFloat(vacationStatus.contractHoursPerWeekFullTime || "0") / 5;
        if (vacationStatus.contractVacationMinutes < 2500) {
            return toFixedIfNecessary(payrollBreakdown.usedVacationMinutes / 60, 2);
        }
        return toFixedIfNecessary(payrollBreakdown.usedVacationMinutes / 60 / hpd, 2);
    });

    const {
        control,
        watch,
        formState: { isValid },
        trigger,
        setValue,
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: buildPayrollBreakdownFormFromModel(payrollBreakdown, hoursPerDay),
    });

    const deltaWorkHours = watch("deltaWorkHours");
    const deltaWorkMode = watch("deltaWorkMode");
    const usedVacationDays = watch("usedVacationDays");
    const totalWorkedDays = watch("totalWorkedDays");
    const absences = watch("absences");
    const bonuses = watch("bonuses");
    const effectiveFees = watch("effectiveFees");
    const dynamicFees = watch("dynamicFees");
    const overtimePaid100Hours = watch("overtimePaid100Hours");
    const overtimePaid125Hours = watch("overtimePaid125Hours");
    const totalWork115Hours = watch("totalWork115Hours");
    const totalWork125Hours = watch("totalWork125Hours");
    const totalWork150Hours = watch("totalWork150Hours");
    const totalWork200Hours = watch("totalWork200Hours");

    const { data: overtimeData } = useQuery(GET_MISSION_OVERTIME_STATUS, {
        variables: {
            missionId: payrollBreakdown.missionInfo?.id,
        },
        skip: !payrollBreakdown?.missionInfo.id,
        fetchPolicy: "cache-and-network",
    });
    const { data: missionVacationAccumulatedMinutes } = useQuery(
        GET_MISSION_VACATION_ACCUMULATION_MINUTES_FOR_PAYROLL_BREAKDOWN,
        {
            variables: {
                payrollBreakdownId: payrollBreakdown?.id,
            },
            skip: !payrollBreakdown?.id,
            fetchPolicy: "cache-and-network",
        },
    );
    const endOfMonthVacationTotal = useCallback(
        (inputType: string) => {
            const availableInMinutes =
                (vacationStatus?.availableVacationMinutes || 0) +
                (missionVacationAccumulatedMinutes?.getMissionVacationAccumulationMinutesForPayrollBreakdown ||
                    0);
            const usedInMinutes = (usedVacationDays || 0) * 60 * hoursPerDay;
            const remainingMinutes = availableInMinutes - usedInMinutes;
            if (inputType === "days") {
                return toFixedIfNecessary(remainingMinutes / 60 / hoursPerDay, 2).toString();
            } else if (inputType === "hours") {
                return toFixedIfNecessary(remainingMinutes / 60, 2).toString();
            } else {
                return toFixedIfNecessary(remainingMinutes, 2).toString();
            }
        },
        [usedVacationDays, vacationStatus, hoursPerDay, missionVacationAccumulatedMinutes],
    );

    const getDeltaWork = useCallback(() => {
        if (deltaWorkMode === "minus") {
            return (deltaWorkHours || 0) * -60;
        }
        return (deltaWorkHours || 0) * 60;
    }, [deltaWorkHours, deltaWorkMode]);

    const endOfMonthOvertimeTotal = useMemo(() => {
        const overtime = overtimeData?.getMissionOvertimeStatus.overtimeMinutes;
        if (overtime === undefined) {
            return null;
        }
        return toFixedIfNecessary(overtime + Number(getDeltaWork() || 0), 2);
    }, [overtimeData, getDeltaWork]);

    const formattedEndOfMonthOvertimeTotal = useMemo(
        () => (endOfMonthOvertimeTotal || 0) / 60,
        [endOfMonthOvertimeTotal],
    );

    const overtimePaidNumber = useMemo(
        () => (overtimePaid100Hours || 0) + (overtimePaid125Hours || 0),
        [overtimePaid100Hours, overtimePaid125Hours],
    );

    const overtimeWorkDelta = useMemo(
        () => toFixedIfNecessary(formattedEndOfMonthOvertimeTotal - overtimePaidNumber, 2),
        [formattedEndOfMonthOvertimeTotal, overtimePaidNumber],
    );

    useDeepCompareEffect(() => {
        const data = {
            deltaWork: getDeltaWork(),
            usedVacationMinutes: usedVacationDays
                ? Math.round(usedVacationDays * 60 * hoursPerDay)
                : 0,
            totalWorkedDays: needWorkDays ? Number(totalWorkedDays) : undefined,
            absences,
            bonuses: bonuses,
            effectiveFees: effectiveFees,
            dynamicFees: dynamicFees
                .filter((f) => f.count !== null && f.count !== undefined)
                .map((f) => ({ refId: f.fee.id, count: f.count as number })),
            overtimePaid100: overtimePaid100Hours ? overtimePaid100Hours * 60 : 0,
            overtimePaid125: overtimePaid125Hours ? overtimePaid125Hours * 60 : 0,
            totalWork115: totalWork115Hours ? totalWork115Hours * 60 : 0,
            totalWork125: totalWork125Hours ? totalWork125Hours * 60 : 0,
            totalWork150: totalWork150Hours ? totalWork150Hours * 60 : 0,
            totalWork200: totalWork200Hours ? totalWork200Hours * 60 : 0,
        };
        onChange(data);
        if (isValid) {
            onValidData(data);
        } else {
            onValidData(null);
        }
    }, [
        isValid,
        deltaWorkHours,
        deltaWorkMode,
        usedVacationDays,
        totalWorkedDays,
        onValidData,
        onChange,
        needWorkDays,
        hoursPerDay,
        getDeltaWork,
        absences,
        bonuses,
        effectiveFees,
        dynamicFees,
        overtimePaid100Hours,
        overtimePaid125Hours,
        totalWork115Hours,
        totalWork125Hours,
        totalWork150Hours,
        totalWork200Hours,
    ]);

    const { chainInputFocus } = useChainInputFocus();

    const maxWorkDays = useMemo(() => {
        if (!mission?.startDate || !payrollBreakdown) {
            return 0;
        }
        return calculatePayrollBreakdownMaxWorkDays({
            contractStart: mission.startDate,
            contractEnd: mission.endDate,
            payrollBreakdownStart: payrollBreakdown.startDate,
            payrollBreakdownEnd: payrollBreakdown.endDate,
        });
    }, [mission?.endDate, mission?.startDate, payrollBreakdown]);

    const overtimePaidAttributeName = useMemo(() => {
        switch (overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent || 100) {
            case 100:
                return "overtimePaid100Hours";
            case 125:
                return "overtimePaid125Hours";
            default:
                throw new Error("Invalid overtimePaidAtPercent value");
        }
    }, [overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent]);

    const payrollBreakdownExtrasProps = useMemo<PayrollBreakdownExtrasProps>(
        () => ({
            control: control,
            clientDeductions: mission?.currentContract?.clientDeductions || [],
            payrollBreakdown,
            setCustomSummaryRows,
            canManageAbsences: !isEmptyPeriod && !isFromTimesheet,
            overtime: {
                update: (newValue) => setValue(overtimePaidAttributeName, newValue),
                remove: () => setValue(overtimePaidAttributeName, undefined),
                initialData: overtimePaidNumber,
                overtimeWorkDelta: overtimeWorkDelta,
                hourlyInvoiceAmount:
                    ((overtimeData?.getMissionOvertimeStatus.overtimeHourlyInvoiceAmount || 0) *
                        (overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent || 100)) /
                    100,
                formattedEndOfMonthOvertimeTotal,
                cctEditionId: mission?.currentContract?.cctEditionId || "",
            },
        }),
        [
            control,
            mission?.currentContract?.clientDeductions,
            mission?.currentContract?.cctEditionId,
            payrollBreakdown,
            setCustomSummaryRows,
            isEmptyPeriod,
            isFromTimesheet,
            overtimePaidNumber,
            overtimeWorkDelta,
            overtimeData?.getMissionOvertimeStatus.overtimeHourlyInvoiceAmount,
            overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent,
            formattedEndOfMonthOvertimeTotal,
            setValue,
            overtimePaidAttributeName,
        ],
    );

    useEffect(() => {
        if (usedVacationInputValue == null || isNaN(usedVacationInputValue)) {
            setValue("usedVacationDays", undefined);
        } else if (vacationInputType === "days") {
            setValue("usedVacationDays", usedVacationInputValue);
        } else if (vacationInputType === "hours") {
            setValue("usedVacationDays", usedVacationInputValue / hoursPerDay);
        } else {
            setValue("usedVacationDays", usedVacationInputValue / 60 / hoursPerDay);
        }
    }, [usedVacationInputValue, vacationInputType, hoursPerDay, setValue]);

    // Changing the delta work flips minWorkDays, which is a validation rule of the totalWorkedDays
    // field, so that field is the one to revalidate for its error message to be recorded.
    // overtimePaid100Hours, the previous target, is registered by no Controller: triggering it only
    // recomputed isValid, which silently unmounted the summary and greyed out the submit button
    // without telling the client what was expected. The first run is skipped because this effect
    // also fires on mount, and a payroll breakdown nobody has touched yet must not open in error.
    const isInitialDeltaWork = useRef(true);
    useEffect(() => {
        if (isInitialDeltaWork.current) {
            isInitialDeltaWork.current = false;
            return;
        }
        void trigger("totalWorkedDays");
    }, [deltaWorkHours, trigger]);

    const handleVacationTypeChange = useCallback(
        (newType: "days" | "hours" | "minutes") => {
            const currentMinutes = (() => {
                if (usedVacationInputValue == null || isNaN(usedVacationInputValue)) return 0;
                if (vacationInputType === "days") return usedVacationInputValue * hoursPerDay * 60;
                if (vacationInputType === "hours") return usedVacationInputValue * 60;
                return usedVacationInputValue;
            })();
            if (newType === "days") {
                setUsedVacationInputValue(toFixedIfNecessary(currentMinutes / hoursPerDay / 60, 2));
            } else if (newType === "hours") {
                setUsedVacationInputValue(toFixedIfNecessary(currentMinutes / 60, 2));
            } else {
                setUsedVacationInputValue(toFixedIfNecessary(currentMinutes, 0));
            }
            setVacationInputType(newType);
        },
        [usedVacationInputValue, vacationInputType, hoursPerDay],
    );

    const openMarkdownLink = useOpenMarkdownLink();

    return (
        <VStack v={1}>
            {isFromTimesheet ? (
                <VStack h="2/3">
                    <PayrollBreakdownMonthlyInfo
                        payrollBreakdown={payrollBreakdown}
                        disabled={timeSheetsNeedCompletion}
                    />
                    <Gap gap={0.25} />
                    <PayrollBreakdownTotalInfo
                        payrollBreakdown={payrollBreakdown}
                        vacationStatus={vacationStatus}
                        disabled={timeSheetsNeedCompletion}
                    />
                    <Gap gap={1} />
                    <PayrollBreakdownCommon
                        needWorkDays={false}
                        control={control}
                        chainInputFocus={chainInputFocus}
                        maxWorkDays={maxWorkDays}
                        minWorkDays={(deltaWorkHours || 0) > 0 ? 1 : undefined}
                        dynamicFees={dynamicFees}
                        clientDeductions={mission?.currentContract?.clientDeductions || []}
                        payrollBreakdownType="monthly"
                    />
                    <Gap gap={1} />
                    {!timeSheetsNeedCompletion && (
                        <PayrollBreakdownExtras {...payrollBreakdownExtrasProps} />
                    )}
                </VStack>
            ) : (
                <VStack h="2/3">
                    {!isEmptyPeriod && (
                        <>
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.monthly.delta_work.title")}
                            </BodyMediumText>
                            <MarkdownView
                                onLinkPress={openMarkdownLink}
                                initialNumberOfLines={1}
                                markdownStyle={{
                                    paragraph: { ...fonts.bodySmall, color: colors.greyAccessible },
                                }}
                                markdown={i18n.t(
                                    "payroll_breakdown.monthly.delta_work.description",
                                )}
                            />
                            <Gap gap={0.5} />
                            <HStack fit="center" gap={0}>
                                <Controller
                                    name="deltaWorkMode"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <DropdownField
                                            value={value}
                                            onSelect={onChange}
                                            items={[
                                                {
                                                    label: i18n.t(
                                                        "payroll_breakdown.field_delta_work_mode_plus",
                                                    ),
                                                    value: "plus",
                                                },
                                                {
                                                    label: i18n.t(
                                                        "payroll_breakdown.field_delta_work_mode_minus",
                                                    ),
                                                    value: "minus",
                                                },
                                            ]}
                                            style={{ flex: 1, flexGrow: 2, flexShrink: 1 }}
                                        />
                                    )}
                                />
                                <Gap gap="1/3" />
                                <Controller
                                    name="deltaWorkHours"
                                    control={control}
                                    render={({
                                        field: { onChange, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <QuantityField
                                            {...chainInputFocus(name)}
                                            value={value}
                                            onChange={onChange}
                                            error={!!error?.message}
                                            showErrorText={false}
                                            decimalPlaces={2}
                                            style={{
                                                flex: 0,
                                                flexGrow: 1,
                                                maxWidth: 100,
                                                minWidth: 100,
                                            }} // minWidth is needed to prevent problem of focus on web
                                        />
                                    )}
                                />
                            </HStack>
                            <Gap gap="2/3" />
                            <FullWidthLine color={colors.accentLighter} />
                            <Gap gap="2/3" />
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.monthly.overtime_paid.title")}
                            </BodyMediumText>
                            <MarkdownView
                                onLinkPress={openMarkdownLink}
                                initialNumberOfLines={1}
                                markdownStyle={{
                                    paragraph: { ...fonts.bodySmall, color: colors.greyAccessible },
                                }}
                                markdown={i18n.t(
                                    "payroll_breakdown.monthly.overtime_paid.description",
                                )}
                            />
                            <Gap gap="1/3" />
                            <>
                                <Controller
                                    name="totalWork115Hours"
                                    control={control}
                                    rules={{
                                        required: false,
                                    }}
                                    render={({
                                        field: { onChange, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <QuantityField
                                            {...chainInputFocus(name)}
                                            isSelfContained
                                            style={{
                                                borderBottomRightRadius: 0,
                                                borderBottomLeftRadius: 0,
                                            }}
                                            label={i18n.t("payroll_breakdown.overtime_115")}
                                            value={value}
                                            decimalPlaces={2}
                                            onChange={onChange}
                                            error={error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="totalWork125Hours"
                                    control={control}
                                    rules={{
                                        required: false,
                                    }}
                                    render={({
                                        field: { onChange, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <QuantityField
                                            {...chainInputFocus(name)}
                                            isSelfContained
                                            style={{
                                                borderBottomRightRadius: 0,
                                                borderBottomLeftRadius: 0,
                                            }}
                                            label={i18n.t("payroll_breakdown.overtime_125")}
                                            value={value}
                                            decimalPlaces={2}
                                            onChange={onChange}
                                            error={error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="totalWork150Hours"
                                    control={control}
                                    rules={{
                                        required: false,
                                    }}
                                    render={({
                                        field: { onChange, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <QuantityField
                                            {...chainInputFocus(name)}
                                            isSelfContained
                                            style={{
                                                marginTop: -2,
                                                borderTopRightRadius: 0,
                                                borderTopLeftRadius: 0,
                                            }}
                                            label={i18n.t("payroll_breakdown.overtime_150")}
                                            value={value}
                                            onChange={onChange}
                                            decimalPlaces={2}
                                            error={error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="totalWork200Hours"
                                    control={control}
                                    rules={{
                                        required: false,
                                    }}
                                    render={({
                                        field: { onChange, value, name },
                                        fieldState: { error },
                                    }) => (
                                        <QuantityField
                                            {...chainInputFocus(name)}
                                            isSelfContained
                                            style={{
                                                marginTop: -2,
                                                borderTopRightRadius: 0,
                                                borderTopLeftRadius: 0,
                                            }}
                                            label={i18n.t("payroll_breakdown.overtime_200")}
                                            value={value}
                                            onChange={onChange}
                                            decimalPlaces={2}
                                            error={error?.message}
                                        />
                                    )}
                                />
                            </>
                            <Gap gap={1} />
                            <FullWidthLine color={colors.accentLighter} />
                            <Gap gap="2/3" />
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.monthly.vacations.title")}
                            </BodyMediumText>
                            <Gap gap={0.5} />
                            <Controller
                                name="usedVacationDays"
                                control={control}
                                rules={{
                                    validate: {
                                        maxPerWeek: (value) => {
                                            return value && value > maxWorkDays
                                                ? i18n.t(
                                                      "payroll_breakdown.error.max_vacation_days",
                                                      { maxWorkDays },
                                                  )
                                                : undefined;
                                        },
                                    },
                                }}
                                render={({ fieldState: { error } }) => (
                                    <>
                                        <View
                                            style={{ flexDirection: "row", alignItems: "center" }}
                                        >
                                            <QuantityField
                                                {...chainInputFocus("usedVacations")}
                                                value={usedVacationInputValue}
                                                onChange={setUsedVacationInputValue}
                                                decimalPlaces={
                                                    ["days", "hours"].includes(vacationInputType)
                                                        ? 2
                                                        : 0
                                                }
                                                showErrorText={false}
                                                style={{
                                                    alignSelf: "flex-start",
                                                    flex: 1,
                                                    minWidth: "50%",
                                                }}
                                                error={!!error?.message}
                                            />
                                            <Gap gap={1 / 2} />
                                            <DropdownField<"days" | "hours" | "minutes">
                                                numberOfLines={1}
                                                style={{
                                                    flexShrink: 1,
                                                    flexGrow: 1,
                                                    flexBasis: "75%",
                                                }}
                                                items={[
                                                    {
                                                        label: i18n.t(
                                                            "payroll_breakdown.vacation_by_days",
                                                        ),
                                                        value: "days",
                                                    },
                                                    {
                                                        label: i18n.t(
                                                            "payroll_breakdown.vacation_by_hours",
                                                        ),
                                                        value: "hours",
                                                    },
                                                    {
                                                        label: i18n.t(
                                                            "payroll_breakdown.vacation_by_minutes",
                                                        ),
                                                        value: "minutes",
                                                    },
                                                ]}
                                                onSelect={handleVacationTypeChange}
                                                value={vacationInputType}
                                            />
                                        </View>
                                        {error?.message && (
                                            <BodySmallText color={colors.error}>
                                                {error.message}
                                            </BodySmallText>
                                        )}
                                    </>
                                )}
                            />
                            <Gap gap="2/3" />
                            <BodySmallText color={colors.greyAccessible}>
                                {i18n.t(
                                    vacationInputType === "days"
                                        ? "payroll_breakdown.used_vacation_days_info"
                                        : vacationInputType === "hours"
                                          ? "payroll_breakdown.used_vacation_hours_info"
                                          : "payroll_breakdown.used_vacation_minutes_info",
                                    {
                                        count: endOfMonthVacationTotal(vacationInputType),
                                        date: formatDate(
                                            new Date(payrollBreakdown.endDate),
                                            SHORT_DATE,
                                        ),
                                    },
                                )}
                            </BodySmallText>
                            <Gap gap={1} />
                            <PayrollBreakdownCommon
                                needWorkDays={needWorkDays}
                                control={control}
                                chainInputFocus={chainInputFocus}
                                maxWorkDays={maxWorkDays}
                                minWorkDays={(deltaWorkHours || 0) > 0 ? 1 : undefined}
                                dynamicFees={dynamicFees}
                                clientDeductions={mission?.currentContract?.clientDeductions || []}
                                payrollBreakdownType="monthly"
                            />
                            <Gap gap={1} />
                            <PayrollBreakdownExtras {...payrollBreakdownExtrasProps} />
                        </>
                    )}
                </VStack>
            )}
        </VStack>
    );
};

export default PayrollBreakdownEditorMonthly;
